<script setup lang="ts">
import {
  buildYouTubeThumbnailUrl,
  normalizeYouTubeVideoId,
} from "#bb/lib/youtube";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);

const source = field<string>("source", "");
const title = field<string>("title", "YouTube video");
const privacyEnhanced = field<boolean>("privacyEnhanced", true);
const autoplay = field<boolean>("autoplay", false);
const controls = field<boolean>("controls", true);
const loop = field<boolean>("loop", false);
const muted = field<boolean>("muted", false);
const startSeconds = field<number>("startSeconds", 0);
const width = field<number>("width", 0);
const height = field<number>("height", 0);

const normalizedVideoId = computed(() => normalizeYouTubeVideoId(source.value));
const thumbnailUrl = computed(() => buildYouTubeThumbnailUrl(source.value));
const sourceError = computed(() => {
  if (!source.value.trim() || normalizedVideoId.value) return undefined;
  return t("invalidSource");
});
</script>

<template>
  <EditorSection :title="t('videoSection')" icon="lucide:youtube" accent="media">
    <EditorField
      :label="t('sourceLabel')"
      :hint="t('sourceHint')"
      :error="sourceError"
    >
      <Input
        v-model="source"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="t('sourcePlaceholder')"
        autocomplete="url"
      />
    </EditorField>

    <div
      v-if="normalizedVideoId && thumbnailUrl"
      class="overflow-hidden rounded-md border bg-card"
    >
      <div class="aspect-video overflow-hidden bg-muted">
        <img
          :src="thumbnailUrl"
          :alt="t('previewAlt')"
          class="size-full object-cover"
        />
      </div>
      <div class="flex items-center gap-2 px-2.5 py-2 text-[11.5px] text-muted-foreground">
        <Icon name="lucide:circle-check" class="size-3.5 text-primary" />
        <span>{{ t("validSource", { id: normalizedVideoId }) }}</span>
      </div>
    </div>

    <div
      v-else-if="!source.trim()"
      class="flex items-center gap-2 rounded-md border border-dashed bg-muted/40 px-2.5 py-2 text-[11.5px] text-muted-foreground"
    >
      <Icon name="lucide:link" class="size-3.5" />
      <span>{{ t("emptySource") }}</span>
    </div>

    <EditorField :label="t('titleLabel')" :hint="t('titleHint')">
      <Input
        v-model="title"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="t('titlePlaceholder')"
      />
    </EditorField>

    <EditorField :label="t('privacyLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("privacyHint") }}</span>
        <Switch v-model="privacyEnhanced" />
      </div>
    </EditorField>
  </EditorSection>

  <EditorSection
    :title="t('dimensionsSection')"
    icon="lucide:move-diagonal-2"
    accent="media"
    :default-open="false"
  >
    <EditorField :label="t('widthLabel')" :hint="t('widthHint')">
      <Input
        v-model.number="width"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>

    <EditorField :label="t('heightLabel')" :hint="t('heightHint')">
      <Input
        v-model.number="height"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>

  <EditorSection
    :title="t('playbackSection')"
    icon="lucide:sliders-horizontal"
    accent="media"
    :default-open="false"
  >
    <EditorField :label="t('autoplayLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("autoplayHint") }}</span>
        <Switch v-model="autoplay" />
      </div>
    </EditorField>

    <EditorField :label="t('controlsLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("controlsHint") }}</span>
        <Switch v-model="controls" />
      </div>
    </EditorField>

    <EditorField :label="t('loopLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("loopHint") }}</span>
        <Switch v-model="loop" />
      </div>
    </EditorField>

    <EditorField :label="t('mutedLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("mutedHint") }}</span>
        <Switch v-model="muted" />
      </div>
    </EditorField>

    <EditorField :label="t('startLabel')" :hint="t('startHint')">
      <Input
        v-model.number="startSeconds"
        type="number"
        min="0"
        max="86400"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  videoSection: "YouTube Video"
  dimensionsSection: "Dimensions"
  playbackSection: "Playback"
  sourceLabel: "Video URL or ID"
  sourceHint: "Supports youtube.com watch, embed and shorts URLs, youtu.be links, or an 11-character video ID."
  sourcePlaceholder: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
  invalidSource: "Enter a supported YouTube URL or a valid 11-character video ID."
  emptySource: "Enter a YouTube URL or video ID to preview the video."
  validSource: "Valid YouTube video · {id}"
  previewAlt: "YouTube video thumbnail preview"
  titleLabel: "Accessible Title"
  titleHint: "Describes the iframe for screen-reader users."
  titlePlaceholder: "Product introduction"
  privacyLabel: "Privacy-enhanced mode"
  privacyHint: "Use youtube-nocookie.com for the embed."
  widthLabel: "Width (pixels)"
  widthHint: "Set to 0 to fill the available width."
  heightLabel: "Height (pixels)"
  heightHint: "Set to 0 to use the default 16:9 aspect ratio."
  autoplayLabel: "Autoplay"
  autoplayHint: "Start automatically. Autoplay is always muted."
  controlsLabel: "Player controls"
  controlsHint: "Show YouTube playback controls."
  loopLabel: "Loop video"
  loopHint: "Restart the video after it finishes."
  mutedLabel: "Start muted"
  mutedHint: "Begin playback without sound."
  startLabel: "Start Time (seconds)"
  startHint: "Start between 0 and 86,400 seconds."
</i18n>
